// The boundary between the snippet runtime (producer, debug mode only) and the on-site debugger (consumer).
// tag.js publishes `window.__rmDebug` before it loads the debugger. A runtime without it predates this contract.

export const RM_DEBUG_CONTRACT_VERSION = 2

export type DebugMode = 'published' | 'draft'

export type SegmentId = string
export type DimensionId = string
export type CampaignId = string
export type VariantId = string

// `null` means the value is not known yet (UNKNOWN in the runtime's three-valued logic).
export type Tri = boolean | null

export interface DebugSessionInfo {
  teamPid: string
  mode: DebugMode
  pageLoadedAt: number
  apiUrl: string
  dashboardUrl: string
  token: string | null
  sandbox: 'self' | 'impersonation'
  // The item a dashboard launch asked to open (one-shot, from the `rm_debug_focus` fragment); null otherwise.
  focus: DebugFocus | null
}

export type DebugFocusKind = 'campaign' | 'widget' | 'phrase'

export interface DebugFocus {
  kind: DebugFocusKind
  id: string
}

export type SegmentSource =
  | { kind: 'override' }
  | { kind: 'answer'; questionId: string | null; answeredAt: number | null }
  // A segment TypeSafe Jev read from the visitor's free-text answer; `confidence` is 0..1.
  | { kind: 'inferred'; questionId: string; confidence: number }
  | { kind: 'signal'; signal: string; detail: string | null }
  | {
      kind: 'esp'
      integration: string
      detail: string | null
      // Raw provider IDs the matched rule referenced; the debugger resolves them through DebuggerEspCatalog.
      tagIds?: string[]
      listIds?: string[]
      fieldId?: string
    }
  | { kind: 'default' }
  | { kind: 'none' }

export interface SegmentFact {
  dimensionId: DimensionId
  multiple: boolean
  effective: SegmentId[]
  actual: SegmentId[]
  overridden: boolean
  source: SegmentSource
  actualSource: SegmentSource
  pendingQuestionId: string | null
}

// `negated` is set after NOT groups are pushed down to their leaves (De Morgan), so a leaf reads on its own.
export interface SegmentCondition {
  kind: 'segment'
  dimensionId: DimensionId
  segmentId: SegmentId
  negated?: boolean
  passed: Tri
}

export interface PageCondition {
  kind: 'page'
  rule: string
  field?: 'url' | 'path' | 'domain' | 'query' | 'referrer' | 'device' | string
  operator?: string
  value?: string
  domain?: string
  negated?: boolean
  // Query parameter key when `field` is 'query'.
  query?: string
  // Whether the rule checks the current page or any page visited earlier.
  occurrence?: 'current' | 'historical' | string
  passed: boolean
}

export type ConditionFact = SegmentCondition | PageCondition

export interface ConditionGroup {
  mode: 'all' | 'any'
  passed: Tri
  conditions: ConditionFact[]
}

export type CampaignArm = { kind: 'treatment' } | { kind: 'holdback' } | { kind: 'variant'; variantId: VariantId }

export type ChangeKind = 'hide' | 'insert' | 'swap_text' | 'swap_image' | 'text' | 'style' | 'attribute' | 'other'

export interface ChangeFact {
  actionId: string
  variantId: VariantId | null
  selector: string
  kinds: ChangeKind[]
  applied: boolean
  reason: string | null
  elements: Element[]
  edge: boolean
}

export type CampaignStatus =
  | 'applied'
  | 'not_matched'
  | 'holdback'
  | 'other_page'
  | 'no_placement'
  | 'waiting'
  | 'inactive'

export interface CampaignVariantFact {
  variantId: VariantId
  name: string | null
  targeting: ConditionGroup
  matched: Tri
}

export interface CampaignAbTestFact {
  audienceId: string
  testId: string
  run: string | null
  versionId: string
  pinned: boolean
  admitted: boolean
}

export interface CampaignFact {
  campaignId: CampaignId
  status: CampaignStatus
  holdbackPct: number | null
  arm: CampaignArm
  forcedArm: CampaignArm | null
  realVisitorArm: 'holdback' | 'treatment' | null
  matchedVariantId: VariantId | null
  naturalVariantId: VariantId | null
  variants: CampaignVariantFact[]
  abTests: CampaignAbTestFact[]
  pages: ConditionGroup
  changes: ChangeFact[]
}

export interface PhraseFact {
  phraseId: string
  dimensionId: DimensionId | null
  activeSegmentId: SegmentId | null
  text: string
  elements: Element[]
}

export type TriggerKind = 'immediate' | 'time' | 'scroll' | 'exit' | 'click' | 'inline' | 'form' | 'bar' | 'datalayer'

export interface TriggerFact {
  kind: TriggerKind
  threshold: number | null
  progress: number | null
  fired: boolean
  firedAt: number | null
  // The GTM dataLayer event a `datalayer` trigger waits for.
  event?: string | null
}

export type FlowNotRunningReason =
  | { kind: 'visibility'; conditions: ConditionGroup }
  | { kind: 'no_placeholder' }
  | { kind: 'placement_target_missing'; selector: string }
  | { kind: 'dismissed'; scope: 'session' | 'permanent' | 'duration'; until: number | null }
  | { kind: 'device'; device: string }
  | { kind: 'same_type_showing'; widgetId: string }
  | { kind: 'disabled' }
  | { kind: 'display_error'; message: string }

export type FlowStatus = 'showing' | 'waiting' | 'ended' | 'not_running'

// Why a flow that ran on this page stopped without showing anything. `null` when the runtime can't tell.
export type FlowEndedReason =
  | { kind: 'already_known'; dimensionIds: DimensionId[] }
  | { kind: 'completed' }
  | { kind: 'dismissed' }
  // Every branch's conditions failed (runtime `siblings_exhausted`); `nodeIds` are the condition nodes it checked.
  | { kind: 'no_matching_step'; nodeIds: string[] }
  // A condition branch led to the flow's exit node; `nodeIds` are the condition nodes on that branch, in order.
  | { kind: 'exited'; nodeIds: string[] }

// Mirrors the runtime's flowExecutionPath entries; `at` is epoch ms.
export interface FlowPathEntry {
  nodeId: string
  nodeType: string
  at: number
  data: Record<string, unknown>
}

export interface KeptDecisionFact {
  nodeId: string
  nodeType: 'yesno' | 'sg' | 'random' | 'highscore' | 'code'
  kept:
    | 'yes'
    | 'no'
    | `segment:${string}`
    | 'fallback'
    | 'none'
    | 'skip'
    | 'ask'
    | `bucket:${number}`
    | `score:${string}`
    | 'display'
    | 'pass'
  now: KeptDecisionFact['kept']
}

export interface SplitTestFact {
  testKey: string
  resourceType: 'flow' | 'offer' | 'question' | 'widget'
  resourceId: string
  assignedVariantId: string
  forced: boolean
  variants: { id: string; name: string }[]
}

export interface FlowFact {
  widgetId: string
  flowId: string | null
  widgetType: string
  status: FlowStatus
  trigger: TriggerFact | null
  notRunning: FlowNotRunningReason[]
  endedReason: FlowEndedReason | null
  path: FlowPathEntry[]
  kept?: KeptDecisionFact[]
  currentNodeId: string | null
  splitTest: SplitTestFact | null
  element: Element | null
}

export interface PageviewFact {
  url: string
  title: string | null
  referrer: string | null
  at: number
  browser: string | null
  os: string | null
  country: string | null
}

export interface VisitorFact {
  visitorId: string | null
  identified: boolean
  email: string | null
  contactId: string | null
  integration: { name: string; label: string } | null
  tags: string[]
  pendingTags: string[]
  fields: { key: string; label: string; value: string }[]
  answers: { questionId: string; value: unknown; at: number | null }[]
  scores: Record<string, number>
  variables: { key: string; value: unknown; expiresAt: number | null }[]
  pageviews: PageviewFact[]
  firstSeenAt: number | null
  trafficSource: { referrer: string | null; landingPage: string | null; utm: Record<string, string> }
}

export interface ImpersonationFact {
  email: string
  status: 'loading' | 'active' | 'not_found' | 'error'
  name: string | null
  company: string | null
}

export interface IdentityFact {
  source: 'anonymous' | 'cookie' | 'storage' | 'url' | 'form' | 'cross_domain' | 'esp_lookup' | 'impersonation'
  detail: string
  profileSource: string | null
}

export interface FormCaptureFact {
  id: string
  label: string
  selector: string | null
  status: 'observing' | 'captured' | 'ignored' | 'blocked'
  reason: string
  email: string | null
  at: number
}

export interface ConsentFact {
  questionId: string
  state: 'granted' | 'denied'
  source: 'question_checkbox'
  effect: string
}

export interface CrossDomainFact {
  source: 'url' | 'cookie' | null
  status: 'absent' | 'received' | 'ignored'
  decisions: { subject: string; applied: boolean; reason: string }[]
}

export interface GoalFact {
  // Null for a site-wide goal, recorded whether or not a campaign uses it.
  campaignId: string | null
  goalId: string
  eventId: string
  type: string
  met: boolean | null
  reason: string
}

export type ConsentState = 'not_required' | 'pending' | 'granted' | 'denied'
export type ConsentSource = 'command' | 'tcf' | 'gcm' | 'cookie'

// The page's cookie-consent gate ("Wait for cookie consent") and the browser's exclusion, beside question consent.
// `held` counts writes waiting in page memory for a grant; `droppedHeld` counts writes past the 100-write cap.
export interface ConsentGateFacts {
  state: ConsentState
  source: ConsentSource | null
  excluded: boolean
  // Why stats from this page are dropped besides exclusion: `rule:<hostname>` or `ip_rule` (Stop collecting rules),
  // `runtime_override` (RightMessage.disableAnalytics), `query_override` (`__rm_disable_analytics=true`).
  suppression?: string | null
  held: number
  droppedHeld: number
  questions: ConsentFact[]
}

export interface RuntimeDecisionFacts {
  identity: IdentityFact
  consent: ConsentGateFacts
  crossDomain: CrossDomainFact
  forms: FormCaptureFact[]
  goals: GoalFact[]
}

export interface DebugSnapshot {
  version: typeof RM_DEBUG_CONTRACT_VERSION
  session: DebugSessionInfo
  config: Record<string, unknown>
  visitor: VisitorFact
  impersonation: ImpersonationFact | null
  segments: SegmentFact[]
  campaigns: CampaignFact[]
  phrases: PhraseFact[]
  flows: FlowFact[]
  splitTests: SplitTestFact[]
  liquidContext: Record<string, unknown>
  decisions?: RuntimeDecisionFacts
  draftReloadPending?: boolean
}

export type WriteChannel =
  | 'middleman'
  | 'webhook'
  | 'email'
  | 'ingestion'
  | 'presence'
  | 'sdk'
  | 'dom_event'
  | 'analytics'

export type DebugEventCategory = 'page' | 'visitor' | 'campaign' | 'widget' | 'flow' | 'write' | 'log' | 'error'

export interface DebugEvent {
  seq: number
  at: number
  category: DebugEventCategory
  type: string
  subject: string | null
  message: string | null
  data: unknown
  write?: { channel: WriteChannel; method: string; payload: unknown }
}

export interface StoredModifications {
  text?: string | null
  findReplace?: { find: string; replace: string }[]
  style?: Record<string, string>
  attr?: Record<string, string>
  classes?: { add?: string[]; remove?: string[] }
  visibility?: 'show' | 'hide'
  [key: string]: unknown
}

export interface StoredAction {
  id: string
  $type?: string
  selector?: string
  targeting?: { mode?: string; [key: string]: unknown } | null
  page: { domain: string; path: string }[]
  modifications: StoredModifications
  [key: string]: unknown
}

export interface StoredVariant {
  id: string
  name?: string
  rules?: { all?: Record<string, unknown>[]; any?: Record<string, unknown>[] }
  actions: StoredAction[]
  [key: string]: unknown
}

export interface StoredCampaign {
  id: string
  name?: string
  variants: StoredVariant[]
  [key: string]: unknown
}

export interface DebugCommands {
  setSegmentOverride(dimensionId: DimensionId, segmentIds: SegmentId[] | null): void
  resetSegmentOverrides(): void
  forceCampaignArm(campaignId: CampaignId, arm: CampaignArm | null): void
  // Session-only preview; null restores the audience's natural version.
  forceAudienceVersion(campaignId: CampaignId, audienceId: VariantId, versionId: string | null): void
  previewCampaign(campaign: StoredCampaign): void
  restoreCampaign(campaignId: CampaignId): void
  commitCampaign(campaignId: CampaignId): void
  holdDraftReload(held: boolean): void
  originalContent(element: Element): string | null
  forceSplitTest(testKey: string, variantId: string | null): void
  restartFlows(): void
  clearDismissedWidgets(): void
  // Fires a waiting widget's trigger for this session only.
  showWidgetNow(widgetId: string): void
  impersonate(email: string): void
  detach(): void
  setMode(mode: DebugMode): void
  clearRealVisitorData(): void
  exit(): void
}

export interface RmDebug {
  version: typeof RM_DEBUG_CONTRACT_VERSION
  snapshot(): DebugSnapshot
  events(): DebugEvent[]
  previousPageEvents(): DebugEvent[]
  subscribe(listener: () => void): () => void
  commands: DebugCommands
}

declare global {
  interface Window {
    __rmDebug?: RmDebug
  }
}

// Server endpoints used by the snippet and the debugger. Token travels in `X-RM-Debugger-Token`.
export const DEBUGGER_TOKEN_HEADER = 'X-RM-Debugger-Token'
export const DEBUGGER_TOKEN_FRAGMENT_KEY = 'rm_debug_token'
// Fragment value is `${kind}:${id}`, e.g. `campaign:cpn_5didDYbc`.
export const DEBUGGER_FOCUS_FRAGMENT_KEY = 'rm_debug_focus'
export const DEBUGGER_MODE_COOKIE = 'rm_debug'

export function debuggerFragmentValue(hash: string, key: string): string | null {
  for (const segment of hash.replace(/^#/, '').split('&')) {
    if (!segment.startsWith(`${key}=`)) {
      continue
    }
    try {
      return decodeURIComponent(segment.slice(key.length + 1))
    } catch {
      return null
    }
  }
  return null
}

// "Preview this version on your site": `version:<campaign>:<original|var_…>:<A-D>`, `arm:<campaign>` (held back)
// or `split:<pid>:<control|variant id>`. The runtime applies it once into the debugger sandbox.
export const DEBUGGER_PIN_FRAGMENT_KEY = 'rm_debug_pin'

export type DebugPin =
  | { kind: 'version'; campaignId: string; nodeId: 'original' | `var_${string}`; versionId: 'A' | 'B' | 'C' | 'D' }
  | { kind: 'arm'; campaignId: string }
  | { kind: 'split'; splitTestPid: string; variantId: string }

const DEBUGGER_FRAGMENT_KEYS: Record<string, true> = {
  [DEBUGGER_TOKEN_FRAGMENT_KEY]: true,
  [DEBUGGER_FOCUS_FRAGMENT_KEY]: true,
  [DEBUGGER_PIN_FRAGMENT_KEY]: true
}
const PIN_VERSION_IDS: Record<string, 'A' | 'B' | 'C' | 'D'> = { A: 'A', B: 'B', C: 'C', D: 'D' }

export function encodeDebugPin(pin: DebugPin): string {
  switch (pin.kind) {
    case 'version':
      return `version:${pin.campaignId}:${pin.nodeId}:${pin.versionId}`
    case 'arm':
      return `arm:${pin.campaignId}`
    case 'split':
      return `split:${pin.splitTestPid}:${pin.variantId}`
  }
}

export function decodeDebugPin(value: string | null): DebugPin | null {
  const version = /^version:(cpn_[\w-]+):(?:(original)|var_([\w-]+)):([ABCD])$/.exec(value ?? '')
  const versionId = PIN_VERSION_IDS[version?.[4] ?? '']
  if (version?.[1] && versionId) {
    const nodeId = version[2] ? 'original' : (`var_${version[3] ?? ''}` as const)
    return { kind: 'version', campaignId: version[1], nodeId, versionId }
  }
  const arm = /^arm:(cpn_[\w-]+)$/.exec(value ?? '')
  if (arm?.[1]) {
    return { kind: 'arm', campaignId: arm[1] }
  }
  // Split variant IDs are free strings, so everything after the pid is the variant.
  const split = /^split:([\w-]+):(.+)$/s.exec(value ?? '')
  if (split?.[1] && split[2]) {
    return { kind: 'split', splitTestPid: split[1], variantId: split[2] }
  }
  return null
}

export function stripDebuggerFragment(hash: string): string {
  const fragment = hash
    .replace(/^#/, '')
    .split('&')
    .filter((segment) => !DEBUGGER_FRAGMENT_KEYS[segment.split('=')[0] ?? ''])
    .join('&')
  return fragment ? `#${fragment}` : ''
}

// `undefined` keeps the hash's current focus or pin, so a second call can add the token alone.
export function appendDebuggerFragment(
  hash: string,
  token: string | null,
  focus?: string | null,
  pin?: string | null
): string {
  const page = stripDebuggerFragment(hash).slice(1)
  const segments = page ? [page] : []
  const selectedFocus = focus === undefined ? debuggerFragmentValue(hash, DEBUGGER_FOCUS_FRAGMENT_KEY) : focus
  const selectedPin = pin === undefined ? debuggerFragmentValue(hash, DEBUGGER_PIN_FRAGMENT_KEY) : pin
  if (token !== null) {
    segments.push(`${DEBUGGER_TOKEN_FRAGMENT_KEY}=${encodeURIComponent(token)}`)
  }
  if (selectedFocus !== null) {
    segments.push(`${DEBUGGER_FOCUS_FRAGMENT_KEY}=${encodeURIComponent(selectedFocus)}`)
  }
  if (selectedPin !== null) {
    segments.push(`${DEBUGGER_PIN_FRAGMENT_KEY}=${encodeURIComponent(selectedPin)}`)
  }
  return segments.length ? `#${segments.join('&')}` : ''
}

export interface DebuggerSessionResponse {
  user: { name: string; email: string }
  team: { pid: string; name: string }
  expiresAt: string
  contactEmail: string | null
  integration: { name: string; label: string } | null
}

export interface DebuggerContactLookup {
  found: boolean
  integration: { name: string; label: string } | null
  contact: {
    email: string
    name: string | null
    company: string | null
    tags: string[]
    fields: { key: string; label: string; value: string }[]
  } | null
  profile: unknown
}

export interface DebuggerContactHistory {
  sessions: unknown[]
  answers: unknown[]
}

export interface DebuggerEspCatalog {
  partial?: boolean
  integration: { name: string; label: string } | null
  tags: { id: string; label: string }[]
  lists: { id: string; label: string }[]
  customFields: { id: string; label: string; templatingToken: string | null }[]
}
